import type { components } from '@/api/schema'
import { Archive, CheckCheck, FolderOpen, Tag, Inbox } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ActionOutcome } from './action-outcome'

type Action = components['schemas']['ClassificationAction']
type Decision = components['schemas']['ClassificationDecision']
type Kind = Decision['kind']
const icons = {
  archive: Archive,
  mark_read: CheckCheck,
  folder: FolderOpen,
  label: Tag,
  category: Tag,
  keep: Inbox,
}
const actionNames = {
  archive: 'Archive',
  mark_read: 'Mark as read',
  folder: 'Move to',
  label: 'Add label',
  category: 'Add category',
  keep: 'Keep where it is',
}

// One row per decision the model made; a decision it acted on shows how that went.
type Row = { kind: Kind; action?: Action; decision?: Decision }

function rows(actions: Action[], decisions: Decision[]): Row[] {
  if (!decisions.length) return actions.map((action) => ({ kind: action.kind, action }))
  const matched = new Set<Action>()
  const found = decisions.map((decision) => {
    const action = actions.find(
      (a) =>
        !matched.has(a) &&
        a.kind === decision.kind &&
        (a.target_id ?? null) === (decision.target_id ?? null),
    )
    if (action) matched.add(action)
    return { kind: decision.kind, action, decision }
  })
  return [
    ...found,
    ...actions.filter((a) => !matched.has(a)).map((action) => ({ kind: action.kind, action })),
  ]
}

export function ActionTimeline({
  actions,
  decisions = [],
  preview = false,
  readinessReason,
  nameFor,
}: {
  actions: Action[]
  decisions?: Decision[]
  preview?: boolean
  readinessReason?: string | null
  nameFor?: (kind: Kind, targetId?: string | null) => string
}) {
  const items = rows(actions, decisions)
  if (!items.length)
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <Inbox className="mb-3 size-7 text-muted-foreground" />
        <p className="font-semibold">
          {preview ? 'Would leave this email as is' : 'Nothing was done to this email'}
        </p>
      </div>
    )
  const name = (kind: Kind, targetId?: string | null, targetName?: string | null) =>
    targetName ? actionNames[kind] : (nameFor?.(kind, targetId) ?? actionNames[kind])
  return (
    <div className="sentry-block ph-no-capture">
      <ol>
        {items.map(({ kind, action, decision }, index) => {
          const Icon = icons[kind]
          return (
            <li key={index} className="relative flex gap-4">
              {index < items.length - 1 && (
                <div className="absolute bottom-0 left-5 top-10 w-px bg-border" />
              )}
              <div
                className={`relative flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-background ${action?.status === 'succeeded' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}
              >
                <Icon className="size-4" />
              </div>
              <div className="min-w-0 flex-1 pb-5 pt-2">
                {action ? (
                  <ActionOutcome
                    action={action}
                    name={name(kind, action.target_id, action.target_name)}
                    preview={preview}
                    readinessReason={readinessReason}
                  />
                ) : (
                  decision && (
                    <DecisionOutcome
                      decision={decision}
                      name={name(kind, decision.target_id, decision.target_name)}
                    />
                  )
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function DecisionOutcome({ decision, name }: { decision: Decision; name: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="font-semibold">
        {decision.target_name ? `${name} “${decision.target_name}”` : name}
      </span>
      {decision.kind !== 'keep' && <Badge variant="secondary">No</Badge>}
      {decision.confidence != null && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {Math.round(decision.confidence * 100)}% confident
        </span>
      )}
    </div>
  )
}
